import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  CreateTagRequest,
  ListResponse,
  TagEntityKind,
  TagQuery,
  TagResponse,
  UpdateTagRequest,
} from '@troia-v3/types';

import { TAG_CATALOG_QUERY_KEY } from '@/shared/hooks/useTagCatalog';

import { tagsApi } from '../services/tagsApi';

const QUERY_KEY = 'tags';

/**
 * Caches que uma mudança no catálogo torna obsoletos.
 *
 * `tag-catalog` é chave IRMÃ, não prefixo de `tags` — sem invalidá-la, criar
 * uma tag na tela de Configurações não a faria aparecer no seletor do chat até
 * o `staleTime` de 5 min expirar. As outras três existem porque renomear faz
 * CASCATA: a palavra muda dentro de contatos, tickets, clientes e conversas
 * que já estão na tela.
 */
const invalidarTudoQueDependeDeTag = (queryClient: ReturnType<typeof useQueryClient>): void => {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: TAG_CATALOG_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: ['available-tags'] }); // contrato antigo `string[]`
  queryClient.invalidateQueries({ queryKey: ['contact'] });
  queryClient.invalidateQueries({ queryKey: ['conversations'] });
};

const mensagemDeErro = (error: unknown, padrao: string): string =>
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? padrao;

export const useTags = (params?: TagQuery) =>
  useQuery<ListResponse<TagResponse>>({
    queryKey: [QUERY_KEY, params],
    queryFn: () => tagsApi.list(params),
    staleTime: 30_000,
  });

export const useCreateTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTagRequest) => tagsApi.create(data),
    onSuccess: (tag) => {
      invalidarTudoQueDependeDeTag(queryClient);
      toast.success(`Tag "${tag.name}" criada`);
    },
    onError: (error: unknown) => {
      // O 409 do backend já vem com o texto útil ("A tag X já existe — nomes
      // diferem apenas em acento ou maiúsculas"), que é a explicação que a
      // pessoa precisa para entender por que "vip" foi recusado.
      toast.error(mensagemDeErro(error, 'Erro ao criar tag'));
    },
  });
};

/** Quantos documentos a cascata tocou, em texto que a pessoa entende. */
const resumoDaCascata = (updated: Record<TagEntityKind, number>): string => {
  const partes: string[] = [];
  const rotulos: Array<[TagEntityKind, string, string]> = [
    ['contact', 'contato', 'contatos'],
    ['ticket', 'ticket', 'tickets'],
    ['customer', 'cliente', 'clientes'],
    ['conversation', 'conversa', 'conversas'],
  ];

  for (const [chave, singular, plural] of rotulos) {
    const n = updated[chave];
    if (n > 0) partes.push(`${n} ${n === 1 ? singular : plural}`);
  }

  return partes.join(', ');
};

export const useUpdateTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTagRequest }) => tagsApi.update(id, data),
    onSuccess: ({ tag, updated }) => {
      invalidarTudoQueDependeDeTag(queryClient);
      const cascata = resumoDaCascata(updated);
      toast.success(cascata ? `Tag "${tag.name}" atualizada em ${cascata}` : `Tag "${tag.name}" atualizada`);
    },
    onError: (error: unknown) => {
      toast.error(mensagemDeErro(error, 'Erro ao atualizar tag'));
    },
  });
};

export const useDeleteTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, purge }: { id: string; purge: boolean }) => tagsApi.remove(id, purge),
    onSuccess: (resultado) => {
      invalidarTudoQueDependeDeTag(queryClient);
      const purgados = resultado.purged ? resumoDaCascata(resultado.purged) : '';
      toast.success(purgados ? `Tag excluída e removida de ${purgados}` : 'Tag excluída do catálogo');
    },
    onError: (error: unknown) => {
      toast.error(mensagemDeErro(error, 'Erro ao excluir tag'));
    },
  });
};
